import React, { FC, PropsWithChildren, useMemo, useCallback } from 'react'
import cx from 'classnames'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { Block } from '../../../Availabilities/Time'
import CoreButton from 'components/Core/CoreButton'
import { Event } from '../../../types'
import styles from './styles.module.scss'

dayjs.extend(utc)
dayjs.extend(timezone)

export interface SlotBlock extends Block {
  selected: boolean
  event: Event | null
}

interface SlotProps {
  event: Event
  className?: string
  block: SlotBlock
  timezone: string
  adjustedNow: Date
  onClick: (block: SlotBlock) => void
}

const Slot: FC<PropsWithChildren<SlotProps>> = ({ event, className, block, timezone, adjustedNow, onClick }) => {
  const djsStartDate = useMemo(() => {
    return dayjs(block.start).tz(timezone)
  }, [block.start, timezone])

  const djsEndDate = useMemo(() => {
    return dayjs(block.end).tz(timezone)
  }, [block.end, timezone])

  const handleClick = useCallback(() => {
    onClick(block)
  }, [block, onClick])

  const disabled = useMemo(() => {
    if (block.event) return false

    return block.start.getTime() < adjustedNow.getTime()
  }, [adjustedNow, block])

  const text = [
    block.event && event.id != block.event.id ? 'Site visit scheduled' : null,
    block.event && event.id == block.event.id ? 'Rescheduled site visit' : null,
    [djsStartDate, djsEndDate].map((e) => e.format('h:mm A')).join(' - '),
  ]
    .filter(Boolean)
    .join(' • ')

  return (
    <li className={cx(styles.slot, className)}>
      <CoreButton
        className={cx({ 'tw:text-neutrals-500': !!block.event })}
        type="button"
        size="m"
        kind={block.selected && !block.event ? 'significant' : 'ghost'}
        text={text}
        onClick={handleClick}
        disabled={disabled}
      />
    </li>
  )
}

export default Slot
